import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import stilePulsanti from "../interfaccia/stilePulsanti";

const ACTION_OPTIONS = [
  {
    value: "assign",
    label: "Assegna articoli vacanti",
    description:
      "Prenota testi/immagini solo sugli articoli che risultano scoperti.",
  },
  {
    value: "reassign",
    label: "Riassegna articoli",
    description:
      "Forza una nuova assegnazione anche se gli articoli sono già prenotati.",
  },
  {
    value: "clear",
    label: "Rimuovi assegnazioni",
    description: "Libera le prenotazioni senza assegnare a nessun utente.",
  },
];

const SCOPE_OPTIONS = [
  { value: "both", label: "Testi + immagini" },
  { value: "texts", label: "Solo testi" },
  { value: "images", label: "Solo immagini" },
];

const TEXT_COLOR = "#1976d2";
const TEXT_IMPORT_COLOR = "#90caf9";
const IMAGE_COLOR = "#fdd835";
const PREVIEW_TEXT_COLOR = "#ef6c00";
const PREVIEW_IMAGE_COLOR = "#ffb74d";

function formatInteger(value) {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed)) {
    return "0";
  }
  return parsed.toLocaleString("it-IT");
}

function formatTextSummary(total, imported) {
  const normalizedTotal = Math.max(Number(total ?? 0), 0);
  const normalizedImported = Math.min(
    Math.max(Number(imported ?? 0), 0),
    normalizedTotal
  );
  if (normalizedImported > 0) {
    return `${formatInteger(normalizedTotal)} T (${formatInteger(
      normalizedImported
    )} import)`;
  }
  return `${formatInteger(normalizedTotal)} T`;
}

function formatImageSummary(value) {
  return `${formatInteger(value)} I`;
}

function ModaArticoliBulkAssignModal({
  open,
  onClose,
  summary,
  loading = false,
  error = "",
  onRefresh,
  onSubmit,
  userOptions = [],
  saving = false,
}) {
  const [action, setAction] = useState("assign");
  const [scope, setScope] = useState("both");
  const [selectedUser, setSelectedUser] = useState("");

  useEffect(() => {
    if (open) {
      setAction("assign");
      setScope("both");
      setSelectedUser("");
    }
  }, [open]);

  const { buttonClassesWhite, buttonClassesGreen } = stilePulsanti;
  const primaryButtonStyle = {
    backgroundColor: "var(--colTastoVerde, #137752)",
    borderColor: "var(--colTastoVerde, #137752)",
    color: "#fff",
  };

  const {
    total,
    needsTexts,
    needsImages,
    userLoads: summaryUserLoads,
  } = useMemo(() => {
    const usersSource =
      (Array.isArray(summary?.users) && summary.users.length
        ? summary.users
        : Array.isArray(summary?.userLoads) && summary.userLoads.length
        ? summary.userLoads
        : null) ?? [];
    const normalizedUsers = usersSource
      .map((user) => ({
        code: (user.code ?? user.userCode ?? "").trim(),
        displayName:
          user.displayName ??
          user.description ??
          user.code ??
          user.userCode ??
          "",
        pendingTexts: Number(user.pendingTexts ?? user.texts ?? 0),
        pendingTextsImported: Number(
          user.pendingTextsImported ?? user.textsImported ?? 0
        ),
        pendingImages: Number(user.pendingImages ?? user.images ?? 0),
      }))
      .filter((user) => user.code.length);
    return {
      total: summary?.total ?? 0,
      needsTexts: summary?.needsTexts ?? 0,
      needsImages: summary?.needsImages ?? 0,
      userLoads: normalizedUsers,
    };
  }, [summary]);
  const userLoadList = useMemo(() => {
    if (summaryUserLoads.length) {
      return summaryUserLoads;
    }
    if (Array.isArray(userOptions) && userOptions.length) {
      return userOptions
        .filter((option) => option.code)
        .map((option) => ({
          code: option.code,
          displayName: option.description ?? option.code,
          pendingTexts: 0,
          pendingTextsImported: 0,
          pendingImages: 0,
        }));
    }
    return [];
  }, [summaryUserLoads, userOptions]);
  const processedUserLoads = useMemo(() => {
    return userLoadList.map((user) => {
      const pendingTexts = Math.max(Number(user.pendingTexts ?? 0), 0);
      const pendingTextsImported = Math.min(
        Math.max(Number(user.pendingTextsImported ?? 0), 0),
        pendingTexts
      );
      const textFresh = Math.max(pendingTexts - pendingTextsImported, 0);
      const pendingImages = Math.max(Number(user.pendingImages ?? 0), 0);
      const total = textFresh + pendingTextsImported + pendingImages;
      return {
        ...user,
        pendingTexts,
        pendingTextsImported,
        pendingImages,
        textFresh,
        total,
      };
    });
  }, [userLoadList]);
  const baseMaxUserLoad = useMemo(() => {
    if (!processedUserLoads.length) {
      return 0;
    }
    return processedUserLoads.reduce(
      (max, user) => Math.max(max, user.total),
      0
    );
  }, [processedUserLoads]);

  const requiresUser = action !== "clear";
  const hasDocument = typeof document !== "undefined";
  const previewAssignments = useMemo(() => {
    if (!requiresUser) {
      return { texts: 0, images: 0 };
    }
    const includeTexts = scope !== "images";
    const includeImages = scope !== "texts";
    return {
      texts: includeTexts ? Math.max(needsTexts, 0) : 0,
      images: includeImages ? Math.max(needsImages, 0) : 0,
    };
  }, [requiresUser, scope, needsTexts, needsImages]);
  const previewTotals =
    Math.max(previewAssignments.texts, 0) +
    Math.max(previewAssignments.images, 0);
  const previewAwareMaxLoad = useMemo(() => {
    let max = baseMaxUserLoad;
    if (requiresUser && selectedUser && previewTotals > 0) {
      const target = processedUserLoads.find(
        (user) => user.code === selectedUser
      );
      if (target) {
        max = Math.max(max, target.total + previewTotals);
      } else {
        max = Math.max(max, previewTotals);
      }
    }
    if (max === 0 && previewTotals > 0) {
      return previewTotals;
    }
    return max;
  }, [
    baseMaxUserLoad,
    previewTotals,
    processedUserLoads,
    requiresUser,
    selectedUser,
  ]);
  const scopeHasTargets = useMemo(() => {
    if (action !== "assign") {
      return true;
    }
    if (scope === "both") {
      return needsTexts > 0 || needsImages > 0;
    }
    if (scope === "texts") {
      return needsTexts > 0;
    }
    return needsImages > 0;
  }, [action, scope, needsTexts, needsImages]);
  const canSubmit =
    !saving &&
    scopeHasTargets &&
    (!requiresUser || selectedUser.trim().length > 0);
  const isScopeDisabled = (value) => {
    if (action !== "assign") {
      return false;
    }
    if (value === "both") {
      return needsTexts === 0 && needsImages === 0;
    }
    if (value === "texts") {
      return needsTexts === 0;
    }
    return needsImages === 0;
  };
  const renderUserCard = (user) => {
    const textLoad = user.textFresh ?? 0;
    const importedLoad = user.pendingTextsImported ?? 0;
    const imageLoad = user.pendingImages ?? 0;
    const totalTexts = textLoad + importedLoad;
    const denominator = Math.max(previewAwareMaxLoad, 1);
    const textWidth = Math.min(100, (textLoad / denominator) * 100);
    const importWidth = Math.min(100, (importedLoad / denominator) * 100);
    const imageWidth = Math.min(100, (imageLoad / denominator) * 100);
    const isSelected = selectedUser === user.code;
    const showPreview = isSelected && previewTotals > 0;
    const previewTextWidth = showPreview
      ? Math.min(100, (previewAssignments.texts / denominator) * 100)
      : 0;
    const previewImageWidth = showPreview
      ? Math.min(100, (previewAssignments.images / denominator) * 100)
      : 0;
    return (
      <label
        key={user.code}
        className={`flex items-start br3 ba pa2 mb2 pointer ${
          isSelected ? "b--colAlt bg-washed-green" : "b--black-10 bg-near-white"
        }`}
      >
        <input
          type="radio"
          name="bulk-user"
          className="mr2 mt1"
          value={user.code}
          checked={selectedUser === user.code}
          onChange={() => setSelectedUser(user.code)}
          disabled={saving || loading}
        />
        <div className="flex-auto">
          <div className="flex justify-between items-center">
            <span className="b">{user.displayName || user.code}</span>
            <span className="f7 gray">
              {formatTextSummary(totalTexts, importedLoad)} /{" "}
              {formatImageSummary(imageLoad)}
            </span>
          </div>
          <div className="mt2 h1 br-pill bg-light-gray flex overflow-hidden">
            {textWidth > 0 && (
              <div
                style={{
                  width: `${textWidth}%`,
                  backgroundColor: TEXT_COLOR,
                }}
                title="Testi assegnati"
              />
            )}
            {importWidth > 0 && (
              <div
                style={{
                  width: `${importWidth}%`,
                  backgroundColor: TEXT_IMPORT_COLOR,
                }}
                title="Testi importati assegnati"
              />
            )}
            {imageWidth > 0 && (
              <div
                style={{
                  width: `${imageWidth}%`,
                  backgroundColor: IMAGE_COLOR,
                }}
                title="Immagini assegnate"
              />
            )}
            {showPreview && previewTextWidth > 0 && (
              <div
                style={{
                  width: `${previewTextWidth}%`,
                  backgroundColor: PREVIEW_TEXT_COLOR,
                }}
                title="Carico testi dell'operazione"
              />
            )}
            {showPreview && previewImageWidth > 0 && (
              <div
                style={{
                  width: `${previewImageWidth}%`,
                  backgroundColor: PREVIEW_IMAGE_COLOR,
                }}
                title="Carico immagini dell'operazione"
              />
            )}
          </div>
        </div>
      </label>
    );
  };
  useEffect(() => {
    if (!requiresUser && selectedUser) {
      setSelectedUser("");
      return;
    }
    if (
      requiresUser &&
      selectedUser &&
      !userLoadList.some((user) => user.code === selectedUser)
    ) {
      setSelectedUser("");
    }
  }, [requiresUser, selectedUser, userLoadList]);
  const renderUserSelection = () => {
    if (!requiresUser) {
      return (
        <div className="pa3 br2 bg-washed-yellow dark-gray">
          <strong>Attenzione:</strong> questa operazione rimuove le prenotazioni
          selezionate dagli articoli filtrati.
        </div>
      );
    }
    if (!userLoadList.length) {
      return (
        <div className="pa3 br2 bg-washed-yellow dark-gray">
          Nessun utente del team pubblicazioni disponibile per
          l&apos;assegnazione.
        </div>
      );
    }
    return (
      <div>
        <p className="f6 gray mt0 mb3">
          Seleziona l'utente a cui assegnare le lavorazioni.
        </p>
        <div>{processedUserLoads.map((user) => renderUserCard(user))}</div>
        <div className="f7 gray mt3 flex flex-wrap">
          <span className="flex items-center mr3 mb2">
            <span
              className="dib mr1"
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: TEXT_COLOR,
              }}
            />
            Nuovi testi
          </span>
          <span className="flex items-center mr3 mb2">
            <span
              className="dib mr1"
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: TEXT_IMPORT_COLOR,
              }}
            />
            Testi importati
          </span>
          <span className="flex items-center mr3 mb2">
            <span
              className="dib mr1"
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: IMAGE_COLOR,
              }}
            />
            Immagini
          </span>
          <span className="flex items-center mr3 mb2">
            <span
              className="dib mr1"
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: PREVIEW_TEXT_COLOR,
              }}
            />
            Carico testi in anteprima
          </span>
          <span className="flex items-center mr3 mb2">
            <span
              className="dib mr1"
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: PREVIEW_IMAGE_COLOR,
              }}
            />
            Carico immagini in anteprima
          </span>
        </div>
        {!selectedUser && (
          <p className="f7 gray mt2">
            Scegli un utente per procedere con l&apos;operazione.
          </p>
        )}
      </div>
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    onSubmit({
      action,
      scope,
      user: requiresUser ? selectedUser : null,
    });
  };

  if (!open || !hasDocument) {
    return null;
  }

  return createPortal(
    <div className="modal" onClick={onClose}>
      <div
        className="w-90 w-70-ns w-50-l center pa4 br3 shadow-4 sfondoBiancoTema relative"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="bn bg-transparent absolute top-1 right-1 pointer f3"
          onClick={onClose}
          aria-label="Chiudi"
        >
          ×
        </button>
        <h3 className="mt0 mb3">Assegna lavorazioni</h3>
        <form onSubmit={handleSubmit}>
          <div className="mb3">
            <p className="ma0">
              Hai selezionato <strong>{total}</strong>{" "}
              {total === 1 ? "articolo" : "articoli"}.
            </p>
            <p className="ma0 mt2">
              <strong>{needsTexts}</strong>{" "}
              {needsTexts === 1 ? "articolo ha" : "articoli hanno"} bisogno di
              testi (pubblicato = NO e senza prenotazione).
            </p>
            <p className="ma0 mt1">
              <strong>{needsImages}</strong>{" "}
              {needsImages === 1 ? "articolo ha" : "articoli hanno"} bisogno di
              immagini (senza immagini e senza prenotazione).
            </p>
            <div className="mt2 flex items-center">
              <span className="mr2 f6 gray">
                Aggiornato {loading ? "…" : "ora"}
              </span>
              <button
                type="button"
                className={buttonClassesWhite}
                onClick={onRefresh}
                disabled={loading}
              >
                Aggiorna conteggio
              </button>
            </div>
            {error && (
              <p className="dark-red mt2">
                {error}
                {onRefresh && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="button-reset underline pointer dark-red"
                      onClick={onRefresh}
                    >
                      Riprova
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
          <div className="flex flex-column flex-row-ns">
            <div className="w-100 w-third-ns pr0 pr3-ns">
              <div className="mb4">
                <span className="db f6 ttu tracked gray mb2">
                  Tipo di azione
                </span>
                {ACTION_OPTIONS.map((option) => (
                  <label key={option.value} className="db mb2 pointer flex">
                    <input
                      type="radio"
                      name="bulk-action"
                      className="mr2"
                      value={option.value}
                      checked={action === option.value}
                      onChange={() => setAction(option.value)}
                      disabled={saving || loading}
                    />
                    <span>
                      {option.label}
                      <span className="db f7 gray">{option.description}</span>
                    </span>
                  </label>
                ))}
              </div>
              <div className="mb4">
                <span className="db f6 ttu tracked gray mb2">Ambito</span>
                {SCOPE_OPTIONS.map((option) => {
                  const disabled = isScopeDisabled(option.value);
                  return (
                    <label
                      key={option.value}
                      className={`db mb2 pointer flex ${
                        disabled ? "o-50" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="bulk-scope"
                        className="mr2"
                        value={option.value}
                        checked={scope === option.value}
                        onChange={() => setScope(option.value)}
                        disabled={disabled || saving || loading}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
                {action === "assign" && !scopeHasTargets && (
                  <p className="f7 dark-red mt1">
                    Nessun articolo selezionato necessita lavorazioni per questo
                    ambito.
                  </p>
                )}
              </div>
            </div>
            <div className="w-100 w-two-thirds-ns mt4 mt0-ns">
              {renderUserSelection()}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              className={`${buttonClassesWhite} mr2`}
              onClick={onClose}
              disabled={saving}
            >
              Annulla
            </button>
            <button
              type="submit"
              className={buttonClassesGreen}
              style={primaryButtonStyle}
              disabled={!canSubmit}
            >
              {saving ? "Assegno..." : "Salva assegnazioni"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default ModaArticoliBulkAssignModal;
